/**
 * Portal Timetable API Client
 *
 * Single data source for all four timetable views (Room / Teacher / Week / List).
 * One request returns every dated occurrence in a range, already expanded from course group
 * recurrence rules and merged with LessonPortal exceptions — the views differ only in how
 * they render that same array.
 */

// API Gateway URL
const API_BASE_URL = 'https://jrgfnay5w2.execute-api.ap-southeast-2.amazonaws.com/v1';

/**
 * Get the authentication token from the user's session.
 */
const getAuthToken = () => {
  const oidcStorage = sessionStorage.getItem('oidc.user:https://cognito-idp.ap-southeast-2.amazonaws.com/ap-southeast-2_3n9j6gDzL:2c6na652k2lkcsaa1ol8m5chp4');
  if (oidcStorage) {
    try {
      const oidcData = JSON.parse(oidcStorage);
      return oidcData.id_token || oidcData.access_token;
    } catch (e) {
      console.error('Error parsing OIDC storage:', e);
    }
  }
  return null;
};

/**
 * Make an authenticated API request to the timetable endpoint.
 */
const apiRequest = async (operation, data = {}) => {
  const token = getAuthToken();

  if (!token) {
    throw new Error('Not authenticated. Please log in again.');
  }

  const response = await fetch(`${API_BASE_URL}/timetable`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({
      operation,
      data
    })
  });

  const result = await response.json();

  if (!response.ok) {
    throw new Error(result.error || `Request failed with status ${response.status}`);
  }

  return result;
};

// ==================== Timetable API ====================

/**
 * Get every timetable occurrence in a date range.
 *
 * @param {Object} params
 * @param {string[]} params.campusIds - Campuses to load. Empty/omitted means every campus the
 *                                     user is allowed to see.
 * @param {string} params.fromDate - YYYY-MM-DD, inclusive
 * @param {string} params.toDate - YYYY-MM-DD, inclusive (max 92 days from fromDate)
 * @param {Object} params.filters - teacherId / room / year / subjectId / typeId /
 *                                  lessonType / status / includeCancelled
 * @returns {Promise<{occurrences, rooms, teachers, campuses, multiCampus, count}>}
 *
 * `rooms[].key` is qualified by campus, because a room name is only unique within its campus
 * — Claremont's "3" and Vic Park's "Room 3" are different rooms. Match occurrences to columns
 * on `roomKey`, never on the display name.
 */
export const getTimetable = async ({ campusIds = [], fromDate, toDate, filters = {} } = {}) => {
  const result = await apiRequest('getTimetable', { campusIds, fromDate, toDate, filters });
  return {
    occurrences: result.occurrences || [],
    rooms: result.rooms || [],
    teachers: result.teachers || [],
    campuses: result.campuses || [],
    multiCampus: Boolean(result.multiCampus),
    count: result.count || 0
  };
};

/**
 * The signed-in teacher's own classes in a date range (My Timetable).
 *
 * Same occurrence shape as getTimetable, minus the student roll, and only occurrences the
 * caller actually teaches: term dates, holiday weeks, the teacher on each schedule row,
 * relief weeks and single-week changes are already applied by the backend.
 *
 * @param {string} fromDate - YYYY-MM-DD, inclusive
 * @param {string} toDate - YYYY-MM-DD, inclusive
 * @returns {Promise<Array>} occurrences
 */
export const getMyTimetable = async (fromDate, toDate) => {
  const result = await apiRequest('getMyTimetable', { fromDate, toDate });
  return result.occurrences || [];
};

// ==================== Lesson write API ====================
//
// Every one of these writes to LessonPortal only — never to the course group. Cancelling a
// class or moving one week's lesson leaves the recurrence (and everything reading it:
// billing, credits, materials) untouched.

/**
 * Create a lesson.
 *
 * Prefer the intent-revealing wrappers below; use this directly only for cases they don't
 * cover. Pass `courseGroupId` + `originalDate` + `originalKey` to override a generated
 * occurrence, or omit them (with a `title`) for a standalone one-off lesson.
 */
export const createLesson = async (data) => {
  const result = await apiRequest('createLesson', data);
  return result.lesson;
};

export const updateLesson = async (lessonId, updates) => {
  const result = await apiRequest('updateLesson', { lessonId, ...updates });
  return result.lesson;
};

export const deleteLesson = async (lessonId) => {
  const result = await apiRequest('deleteLesson', { lessonId });
  return result.deleted;
};

/**
 * The three fields that identify which generated occurrence a lesson overrides.
 *
 * `originalDate`/`originalKey` are the occurrence's ORIGINAL slot, not where it is being
 * moved to — that is what lets a lesson moved to another day still resolve back to the one
 * it replaces.
 */
const overrideRef = (occurrence) => ({
  courseGroupId: occurrence.courseGroupId,
  originalDate: occurrence.originalDate,
  originalKey: occurrence.originalKey
});

/**
 * Apply changes to a single occurrence, creating the override row or updating the existing
 * one. An occurrence already carrying a `lessonId` has been overridden before.
 *
 * This is the one write path for "change just this class"; the named helpers below are thin
 * wrappers so callers state intent rather than assembling override fields.
 */
export const applyToOccurrence = async (occurrence, changes) => {
  if (occurrence.lessonId) {
    return updateLesson(occurrence.lessonId, changes);
  }
  return createLesson({
    ...overrideRef(occurrence),
    campusId: occurrence.campusId,
    date: occurrence.date,
    startTime: occurrence.startTime,
    endTime: occurrence.endTime,
    room: occurrence.room,
    ...changes
  });
};

/** Cancel one occurrence. It stays on the timetable, struck through. */
export const cancelOccurrence = (occurrence, notes = '') =>
  applyToOccurrence(occurrence, { status: 'cancelled', notes });

/** Flag one occurrence as a no-show. The class stays where it is. */
export const markNoShow = (occurrence, who, notes = '') =>
  applyToOccurrence(occurrence, {
    status: who === 'teacher' ? 'no_show_teacher' : 'no_show_student',
    notes
  });

/** Move one occurrence to a different day/time/room, leaving the rest of the term alone. */
export const moveOccurrence = (occurrence, { date, startTime, endTime, room }) =>
  applyToOccurrence(occurrence, {
    date: date || occurrence.date,
    startTime: startTime || occurrence.startTime,
    endTime: endTime || occurrence.endTime,
    room: room !== undefined ? room : occurrence.room
  });

/** Undo an override, restoring whatever the course group's recurrence generates. */
export const restoreOccurrence = (occurrence) =>
  occurrence.lessonId ? deleteLesson(occurrence.lessonId) : Promise.resolve(null);

/**
 * Add a standalone lesson that belongs to no course group — a trial, an assessment or an
 * ad-hoc class. `guestName` covers a prospective student with no StudentPortal record yet.
 */
export const addOneOffLesson = ({
  campusId, date, startTime, endTime, room, title,
  lessonType = 'trial', status = 'pending',
  teacherIds = [], studentIds = [], guestName = '', notes = ''
}) => createLesson({
  campusId, date, startTime, endTime, room, title,
  lessonType, status, teacherIds, studentIds, guestName, notes
});

/**
 * Change one weekly schedule of a course group for the whole term — the "all events"
 * branch of a drag. This is the one call here that writes to the course group; it is the
 * same write the Course Groups page performs, limited to a single schedule entry.
 *
 * An occurrence that already carries a one-week override is still a valid target: it knows
 * which schedule it came from. Choosing "whole term" on it also retires that override, because
 * "every week should look like this" is the opposite of "this week is different" — leaving the
 * exception in place would move every week except the one the user was looking at.
 *
 * @param {Object} occurrence - a recurring occurrence (has courseGroupId + originalKey)
 * @param {Object} changes - any of dayOfWeek / startTime / endTime / room / teacherId
 */
export const updateSchedule = async (occurrence, changes) => {
  const result = await apiRequest('updateSchedule', {
    courseGroupId: occurrence.courseGroupId,
    originalKey: occurrence.originalKey,
    ...(occurrence.lessonId ? { clearOverrideDate: occurrence.originalDate } : {}),
    ...changes
  });
  return result;
};

/**
 * A recurring occurrence that a whole-term change can apply to.
 *
 * Deliberately does not care whether the occurrence has already been overridden for one week:
 * it still belongs to a weekly schedule, and the user must stay able to say "actually, move
 * the whole term". Special-term slots and standalone lessons have no weekly schedule at all
 * and are the real exclusions.
 */
export const isRecurring = (occurrence) =>
  Boolean(occurrence?.courseGroupId) && !String(occurrence?.originalKey || '').startsWith('slot-');

/**
 * One dated slot of a special-term course group.
 *
 * These have no weekly recurrence: the slot IS the class, so moving it means editing the slot
 * rather than writing an exception on top of one.
 */
export const isSpecialSlot = (occurrence) =>
  Boolean(occurrence?.courseGroupId) && String(occurrence?.originalKey || '').startsWith('slot-');

/**
 * Move one dated slot of a special-term course group.
 *
 * The counterpart of updateSchedule for classes that are dated individually. There is no
 * "this week / whole term" question to ask, because a slot happens exactly once — which is
 * also why the slot is edited directly: an override on top of it would leave the Course
 * Groups page, billing and materials all still reading the date the class no longer runs on.
 *
 * @param {Object} occurrence - a special-term occurrence (originalKey looks like "slot-3")
 * @param {Object} changes - any of date / startTime / endTime / room / teacherId
 */
/**
 * Every one-off change made to a course group from the timetable: weeks cancelled, moved or
 * flagged. The Course Groups page shows these next to the recurrence so an admin editing
 * "Mondays 17:45" can see that next Monday was called off from the other screen.
 */
export const listCourseGroupLessons = async (courseGroupId) => {
  const result = await apiRequest('listCourseGroupLessons', { courseGroupId });
  return result.lessons || [];
};

export const updateSlot = async (occurrence, changes) =>
  apiRequest('updateSlot', {
    courseGroupId: occurrence.courseGroupId,
    originalKey: occurrence.originalKey,
    ...changes
  });

export const hasConflict = (occurrence) => (occurrence?.conflicts || []).length > 0;

/**
 * A stripped block for a teacher's class at a campus outside the current view. It carries
 * no course details and only makes sense in Teacher View — everywhere else it is dropped.
 */
export const isBusy = (occurrence) => Boolean(occurrence?.isBusy);

// ==================== Constants ====================

/**
 * What one occurrence is. Distinct from the course group's class type (Group / 1to1 /
 * 1to2&3) — a regular Group class can have a single makeup occurrence.
 */
export const LESSON_TYPE_OPTIONS = [
  { value: 'regular', label: 'Regular', color: '#3b82f6' },
  { value: 'trial', label: 'Trial', color: '#8b5cf6' },
  { value: 'assessment', label: 'Assessment', color: '#f59e0b' },
  { value: 'makeup', label: 'Makeup', color: '#10b981' }
];

/**
 * Status of one occurrence. There is no 'completed' — the timetable does not track
 * attendance; a no-show is a flag on a class that stays in place.
 */
export const LESSON_STATUS_OPTIONS = [
  { value: 'confirmed', label: 'Confirmed', color: '#10b981' },
  { value: 'pending', label: 'Pending', color: '#f59e0b' },
  { value: 'cancelled', label: 'Cancelled', color: '#9ca3af' },
  { value: 'no_show_student', label: 'No-show (Student)', color: '#ef4444' },
  { value: 'no_show_teacher', label: 'No-show (Teacher)', color: '#dc2626' }
];

export const VIEW_OPTIONS = [
  { value: 'room', label: 'Room View' },
  { value: 'teacher', label: 'Teacher View' },
  // One room (or one teacher) across the whole week — the "how booked is this room" angle,
  // which the day-at-a-time Room View cannot answer.
  { value: 'single', label: 'Per Room' },
  { value: 'week', label: 'Week View' },
  { value: 'list', label: 'List View' }
];

/** Views drawn by GridView, i.e. the ones the orientation toggle applies to. */
export const GRID_VIEWS = ['room', 'teacher', 'single'];

// ==================== Display helpers ====================

export const getLessonTypeLabel = (lessonType) => {
  const option = LESSON_TYPE_OPTIONS.find(o => o.value === lessonType);
  return option ? option.label : lessonType || '';
};

export const getLessonStatusLabel = (status) => {
  const option = LESSON_STATUS_OPTIONS.find(o => o.value === status);
  return option ? option.label : status || '';
};

export const getLessonStatusColor = (status) => {
  const option = LESSON_STATUS_OPTIONS.find(o => o.value === status);
  return option ? option.color : '#6b7280';
};

/**
 * A cancelled occurrence is kept in the data on purpose (it is a tombstone, not a deletion)
 * so the timetable can show that a class was called off rather than silently dropping it.
 */
export const isCancelled = (occurrence) => occurrence?.status === 'cancelled';

export const isNoShow = (occurrence) =>
  occurrence?.status === 'no_show_student' || occurrence?.status === 'no_show_teacher';

/**
 * Teacher names as a single display string.
 */
export const formatTeachers = (occurrence) =>
  (occurrence?.teachers || []).map(t => t.name).filter(Boolean).join(', ');

/**
 * Enrolment against capacity, e.g. "6 / 9". Capacity is unlimited for Group classes, in
 * which case only the current headcount is shown.
 */
export const formatEnrolment = (occurrence) => {
  if (!occurrence) return '';
  const enrolled = occurrence.enrolledCount || 0;
  const capacity = occurrence.capacityLimit;
  return capacity ? `${enrolled} / ${capacity}` : `${enrolled}`;
};

export const isOverCapacity = (occurrence) =>
  Boolean(occurrence?.capacityLimit) && (occurrence.enrolledCount || 0) > occurrence.capacityLimit;

/** Every enrolled student's name, for the lesson detail. */
export const studentNames = (occurrence) =>
  (occurrence?.students || []).map(s => s.name).filter(Boolean);

/**
 * Student names shortened to fit on a timetable card.
 *
 * Who is in the room is the fact a 1:1 or small-group card exists to tell you — "1 / 1" says
 * nothing a teacher wanted to know. Up to three names are shown in full, which covers every
 * 1to1 and 1to2&3 class; a larger group shows the first two and a count, because a card is a
 * few centimetres wide and the full roll is one tap away in the lesson detail.
 *
 * A standalone trial has no enrolment at all, only a guest name, so that is used instead.
 */
export const formatStudentNames = (occurrence) => {
  const names = studentNames(occurrence);
  if (!names.length) return occurrence?.guestName || '';
  if (names.length <= 3) return names.join(', ');
  return `${names.slice(0, 2).join(', ')} +${names.length - 2}`;
};

// ==================== Grouping helpers ====================

/**
 * Group occurrences into the column axis a grid view renders.
 *
 * Room View and Teacher View are the same grid with a different axis, so both go through
 * here rather than each maintaining its own bucketing logic.
 *
 * @param {Array} occurrences
 * @param {'room'|'teacher'} groupBy
 * @param {Array} axis - the rooms/teachers array returned by getTimetable
 * @returns {Map<string, Array>} column key -> occurrences (unassigned under '')
 */
export const groupByAxis = (occurrences, groupBy, axis = []) => {
  const buckets = new Map();
  axis.forEach(column => buckets.set(groupBy === 'teacher' ? column.id : column.key, []));

  occurrences.forEach(occurrence => {
    if (groupBy === 'teacher') {
      const teachers = occurrence.teachers || [];
      if (teachers.length === 0) {
        if (!buckets.has('')) buckets.set('', []);
        buckets.get('').push(occurrence);
        return;
      }
      // A class with two teachers genuinely occupies both of their columns
      teachers.forEach(teacher => {
        if (!buckets.has(teacher.id)) buckets.set(teacher.id, []);
        buckets.get(teacher.id).push(occurrence);
      });
      return;
    }

    const key = occurrence.roomKey || '';
    if (!buckets.has(key)) buckets.set(key, []);
    buckets.get(key).push(occurrence);
  });

  return buckets;
};

/**
 * Group occurrences by date, for Week View.
 *
 * @returns {Map<string, Array>} YYYY-MM-DD -> occurrences
 */
export const groupByDate = (occurrences) => {
  const buckets = new Map();
  occurrences.forEach(occurrence => {
    const date = occurrence.date;
    if (!buckets.has(date)) buckets.set(date, []);
    buckets.get(date).push(occurrence);
  });
  return buckets;
};

const timetableApi = {
  getTimetable,
  createLesson,
  applyToOccurrence,
  updateLesson,
  deleteLesson,
  cancelOccurrence,
  markNoShow,
  moveOccurrence,
  restoreOccurrence,
  addOneOffLesson,
  updateSchedule,
  updateSlot,
  listCourseGroupLessons,
  isRecurring,
  isSpecialSlot,
  studentNames,
  formatStudentNames,
  LESSON_TYPE_OPTIONS,
  LESSON_STATUS_OPTIONS,
  VIEW_OPTIONS,
  GRID_VIEWS,
  getLessonTypeLabel,
  getLessonStatusLabel,
  getLessonStatusColor,
  isCancelled,
  isNoShow,
  formatTeachers,
  formatEnrolment,
  isOverCapacity,
  groupByAxis,
  groupByDate
};

export default timetableApi;
